import React from "react";
import {
  Activity,
  Award,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  HeartPulse,
  SearchCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import { computeTodaysFocus } from "@/components/assessment/assessmentScoring";
import { getEvidenceMapping } from "@/components/assessment/evidenceMapping";
import { sexLabel } from "@/components/assessment/participantIdentity";
import { AppCard, AppPageHeader, StatCard } from "@/components/daily/CompanionAppUi";

const greeting = (name) => {
  const hour = new Date().getHours();
  const period = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return `${period}${name ? `, ${name}` : ""}`;
};

const formatEvent = (event) => {
  if (!event) return null;
  const date = new Date(`${event.date}T${event.time || "12:00"}:00`);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const CompanionProfilePageV2 = ({
  assessment,
  plan,
  summary,
  entries,
  biometrics,
  progress,
  nextEvent,
  nextEventGuidance,
  priorities,
  onNavigate,
  onViewAssessment,
}) => {
  const identity = assessment.identity || {};
  const focus = computeTodaysFocus(assessment.answers);
  const mapping = getEvidenceMapping(assessment.profile.key);
  const latest = entries.find((entry) => entry.completed) || entries[0] || null;
  const hasCurrentState = Boolean(plan?.currentState?.system || latest?.system);
  const baselineStateName = mapping?.stateName || assessment.profile.title;
  const currentSystem = plan?.currentState?.system || latest?.system || baselineStateName;
  const currentLabels = plan?.currentState?.labels || latest?.reportedStates || [];
  const currentStateExplanation =
    plan?.stateInterpretation?.participantExplanation ||
    mapping?.stateDescription ||
    assessment.profile.body;
  const earnedBadges = summary.badges.filter((badge) => badge.earned);

  return (
    <div data-testid="companion-profile-page">
      <AppPageHeader
        eyebrow="APOS Companion"
        title={greeting(identity.preferredName)}
        body="Your baseline, current response, schedule, participation, and personal response learning are organized here."
      />

      <AppCard tone="action" className="overflow-hidden">
        <div className="grid gap-6 md:grid-cols-[1.25fr_0.75fr] md:items-start">
          <div>
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white">
                <Activity size={23} className="forr-text-teal-deep" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.19em] forr-text-mute">
                  Current nervous-system context
                </div>
                <h2 className="mt-2 font-display text-3xl forr-text-ink">{currentSystem}</h2>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed forr-text-soft sm:text-base">
              {currentStateExplanation}
            </p>
            <div className="mt-5 rounded-[22px] bg-white/75 p-5">
              <div className="flex items-center gap-3">
                <SearchCheck size={21} className="forr-text-teal-deep" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.19em] forr-text-mute">
                    APOS learning
                  </div>
                  <div className="mt-1 font-display text-2xl forr-text-ink">
                    {progress.observationsCount} response observation
                    {progress.observationsCount === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed forr-text-soft">
                APOS does not turn your nervous system into one score. It learns from comparable situations: what you reported, what you did, the relative dose, and what changed afterward.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="rounded-2xl p-3 forr-surface-tint">
                  <div className="font-display text-xl forr-text-ink">{progress.completedCount}</div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">
                    Completed inputs
                  </div>
                </div>
                <div className="rounded-2xl p-3 forr-surface-tint">
                  <div className="font-display text-xl forr-text-ink">{progress.patternsIdentified}</div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">
                    Patterns learned
                  </div>
                </div>
                <div className="rounded-2xl p-3 forr-surface-tint">
                  <div className="font-display text-xl forr-text-ink">{progress.daysReturned}</div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">
                    Days returned
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[22px] bg-white/75 p-5">
            <div className="flex items-center gap-3">
              <UserRound size={20} className="forr-text-teal-deep" />
              <div>
                <div className="font-display text-xl forr-text-ink">
                  {identity.fullName || identity.preferredName || "Adaptive Profile"}
                </div>
                <div className="mt-1 text-xs forr-text-mute">
                  Age {identity.age || "—"} · {sexLabel(identity.sex)}
                </div>
              </div>
            </div>
            <div className="mt-5 border-t pt-4" style={{ borderColor: "var(--forr-line-soft)" }}>
              <div className="text-[10px] uppercase tracking-[0.17em] forr-text-mute">
                Baseline pattern
              </div>
              <div className="mt-2 font-display text-xl forr-text-ink">{assessment.profile.title}</div>
              <div className="mt-2 text-sm leading-relaxed forr-text-soft">{baselineStateName}</div>
              <button
                type="button"
                onClick={onViewAssessment}
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep"
              >
                <ClipboardList size={15} /> View assessment
              </button>
            </div>
          </div>
        </div>
      </AppCard>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <AppCard tone="information">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="forr-overline">Current nervous-system check-in</div>
              <h2 className="mt-2 font-display text-2xl forr-text-ink">
                {hasCurrentState ? currentSystem : "No current nervous-system check-in yet"}
              </h2>
            </div>
            <Activity size={23} className="forr-text-teal-deep" />
          </div>
          {currentLabels.length ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {currentLabels.map((label) => (
                <span
                  key={label}
                  className="rounded-full px-3 py-1.5 text-xs forr-text-teal-deep"
                  style={{ backgroundColor: "var(--forr-teal-tint)" }}
                >
                  {label}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm leading-relaxed forr-text-soft">
              Report what is happening now to match mobilization, usable energy, action pressure, behavioral choice, body response, attention, and current capacity.
            </p>
          )}
          <button
            type="button"
            onClick={() => onNavigate(plan ? "plan" : "checkIn")}
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep"
          >
            {plan ? "Open today’s nervous-system state match" : "Start check-in"}{" "}
            <ChevronRight size={15} />
          </button>
        </AppCard>

        <AppCard tone="information">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="forr-overline">Next scheduled activity</div>
              <h2 className="mt-2 font-display text-2xl forr-text-ink">
                {nextEvent?.title || "Nothing scheduled next"}
              </h2>
            </div>
            <CalendarDays size={23} className="forr-text-teal-deep" />
          </div>
          {nextEvent ? (
            <>
              <p className="mt-3 text-xs forr-text-mute">{formatEvent(nextEvent)}</p>
              <p className="mt-4 text-sm leading-relaxed forr-text-soft">
                {nextEventGuidance?.instruction ||
                  "Open Plan to review the event and add it to your device calendar."}
              </p>
            </>
          ) : (
            <p className="mt-4 text-sm leading-relaxed forr-text-soft">
              Add your workouts, sports, recreation, or recovery routines inside Plan.
            </p>
          )}
          <button
            type="button"
            onClick={() => onNavigate("plan")}
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep"
          >
            Open calendar <ChevronRight size={15} />
          </button>
        </AppCard>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          icon={Award}
          value={summary.points}
          label="Participation rewards"
          detail="Permanent participation credit; no missed-day penalty."
        />
        <StatCard
          icon={CheckCircle2}
          value={summary.completed.length}
          label="Completed this week"
          detail="Completion records participation, not automatic adaptation."
        />
        <StatCard
          icon={SearchCheck}
          value={summary.responseChecks}
          label="Response observations"
          detail="These observations teach APOS what changes for you."
        />
        <StatCard
          icon={HeartPulse}
          value={biometrics?.heartRate || biometrics?.bpm || "—"}
          label="Latest pulse"
          detail={
            biometrics?.hrvMs
              ? `HRV estimate ${Math.round(biometrics.hrvMs)} ms`
              : "Optional supporting context."
          }
        />
      </div>

      <AppCard className="mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">Assessment baseline</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">{assessment.profile.title}</h2>
          </div>
          <ClipboardList size={22} className="forr-text-teal-deep" />
        </div>
        <p className="mt-4 text-sm leading-relaxed forr-text-soft">{focus.text}</p>
        <p className="mt-3 text-xs leading-relaxed forr-text-mute">
          The baseline is a multidimensional profile. Current nervous-system state and response learning are kept separate rather than being combined into one nervous-system score.
        </p>
        <button
          type="button"
          onClick={onViewAssessment}
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep"
        >
          Read saved assessment <ChevronRight size={15} />
        </button>
      </AppCard>

      <AppCard tone="information" className="mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">Milestones</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              {earnedBadges.length} milestone{earnedBadges.length === 1 ? "" : "s"} earned
            </h2>
          </div>
          <Award size={23} className="forr-text-teal-deep" />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {summary.badges.map((badge) => (
            <div
              key={badge.key}
              className="rounded-2xl p-2.5 text-center"
              style={{
                backgroundColor: badge.earned
                  ? "var(--forr-teal-tint)"
                  : "var(--forr-canvas-alt)",
                opacity: badge.earned ? 1 : 0.42,
              }}
            >
              <div className="text-2xl" aria-hidden="true">
                {badge.emoji}
              </div>
              <div className="mt-1 text-[9px] leading-tight forr-text-mute">{badge.label}</div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onNavigate("progress")}
          className="mt-5 inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep"
        >
          Open progress <ChevronRight size={15} />
        </button>
      </AppCard>

      <button
        type="button"
        onClick={() => onNavigate("companion")}
        className="mt-4 flex w-full items-center justify-between gap-4 rounded-[24px] p-5 text-left text-white"
        style={{ background: "linear-gradient(135deg, var(--forr-teal-deep), var(--forr-teal))" }}
      >
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
            <Sparkles size={21} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">
              Adaptive Companion
            </div>
            <div className="mt-1 font-display text-xl">
              Ask why you feel something physically or mentally
            </div>
            <div className="mt-1 text-xs text-white/75">
              Companion can connect your reported nervous-system state, body sensations, mental experience, schedule, and the reason behind a suggestion.
            </div>
          </div>
        </div>
        <ChevronRight size={19} />
      </button>

      {priorities?.length > 0 && (
        <AppCard className="mt-4">
          <div className="flex items-center gap-3">
            <BookOpen size={21} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">For you now</div>
              <h2 className="mt-1 font-display text-2xl forr-text-ink">Current priorities</h2>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {priorities.slice(0, 4).map((priority, index) => (
              <div
                key={`${index}-${priority.title || priority.label || "priority"}`}
                className="rounded-2xl p-4 forr-surface-tint"
              >
                <div className="font-medium forr-text-ink">
                  {priority.title || priority.label || "Adaptive priority"}
                </div>
                {(priority.body || priority.detail) && (
                  <p className="mt-1 text-xs leading-relaxed forr-text-mute">
                    {priority.body || priority.detail}
                  </p>
                )}
              </div>
            ))}
          </div>
        </AppCard>
      )}
    </div>
  );
};

export default CompanionProfilePageV2;
